import { PresentationChartIcon, SignOut } from '@phosphor-icons/react';
import {
  Collapse, Divider, Flex, message, Progress
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography } from 'tfc-components';

import { menuSidebarAccount } from 'assets/dummy/sidebar';
import ButtonCustom from 'components/atoms/ButtonCustom';
import { getClientUsageSummary } from 'services/dashboard';
import { getOverViewService } from 'services/overview';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers from 'utils/functions';

const { Panel } = Collapse;

interface UserDropdownProps {
  onLogout?: () => void;
  onClose?: () => void;
}

const isAnyChildActive = (
  items: { path?: string }[],
  pathname: string
) => items.some((child) => child.path === pathname);

const MenuItem = ({
  icon,
  label,
  link,
  target,
  active,
  onClose,
  isParent,
  isChild
}: {
  icon?: React.ReactNode;
  label: string;
  link?: string;
  target?: string
  active?: boolean;
  onClose?: () => void;
  isParent?: boolean;
  isChild?: boolean
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = active ?? location.pathname === link;

  const handleNavigate = () => {
    if (link) {
      if (target) {
        window.open(link, target);
      } else {
        navigate(link);
      }
    }
    onClose?.();
  };

  return (
    <div
      onClick={() => handleNavigate()}
      className={mapModifiers('o-userDropdown_item', isActive && 'active', isParent && 'parent', isChild && 'child')}
    >
      {icon}
      <Typography.Text extendClasses="fs-14x22">
        {label}
      </Typography.Text>
    </div>
  );
};

export const formatQuotaSafe = (
  current?: number | string,
  max?: number | string
): string => {
  const toNumber = (val: number | string | undefined) => Number(val || 0).toLocaleString('en-US');

  return `${toNumber(current)} / ${toNumber(max)}`;
};

const UserDropdown: React.FC<UserDropdownProps> = ({ onLogout, onClose }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const systemOthers = useAppSelector((state: RootState) => state.system.system);
  const location = useLocation();

  const { data: overViewData } = useQuery({
    queryKey: ['get-overview'],
    queryFn: getOverViewService,
  });
  /* Ngày hết hạn */
  const expried = dayjs(overViewData?.planData?.endDate).diff(
    new Date(),
    'second'
  );
  const isExpiredPlan = expried <= 0 && !!overViewData?.planData?.endDate;

  const isInactivePlan = overViewData?.requireDelInfo?.status === 7
    && dayjs(overViewData?.requireDelInfo?.startDate).diff(dayjs()) < 0;

  const { data: usageSummary } = useQuery(
    ['client-usage-summary'],
    () => getClientUsageSummary(),
    {
      onError: () => {
        message.error('Unable to load agent list. Please try again later.');
      }
    }
  );

  return (
    <div className="o-userDropdown">
      {/* Sessions */}
      <div className="o-userDropdown_session">
        <div className="o-userDropdown_sessionHeader">
          <Flex gap={6} align="center">
            <PresentationChartIcon weight="fill" size={24} fill="#fff" />
            <Typography.Text fontweight="700" className="fs-16x24">Sessions</Typography.Text>
          </Flex>
          <Flex gap={6} align="flex-start">
            <Typography.Text fontweight="700">
              {formatQuotaSafe(usageSummary?.ttlSession, usageSummary?.limitSession)}
            </Typography.Text>
          </Flex>
        </div>
        <Progress strokeColor="#FFF" trailColor="#A078FF" percent={usageSummary?.ttlSession ? ((usageSummary?.ttlSession || 0) / (usageSummary?.limitSession || 0)) * 100 : 0} showInfo={false} />
        <Flex justify="flex-end">
          <div />
          <ButtonCustom onClick={() => navigate(ROUTE_PATHS.OVER_VIEW)} className="o-userDropdown_manageBtn">
            Manage
          </ButtonCustom>
        </Flex>
      </div>

      <div className="o-userDropdown_divider" />

      {/* Menu */}
      <div className="o-userDropdown_menu">
        {menuSidebarAccount(systemOthers?.slugPages?.blog || '', isExpiredPlan, isInactivePlan).map((group) => group.items.map((item) => {
          const isParentActive = isAnyChildActive(
            item.items || [],
            location.pathname
          );

          return item.items?.length ? (
            <Collapse
              key={item.key}
              className={mapModifiers(
                'o-userDropdown_collapse',
                isParentActive && 'active'
              )}
              expandIconPosition="right"
              ghost
              defaultActiveKey={isParentActive ? item.key : undefined}
            >
              <Panel
                key={item.key}
                header={(
                  <div className="o-userDropdown_collapseHeader">
                    <MenuItem
                      icon={item.icon?.inactive}
                      label={item.title || ''}
                      active={isParentActive}
                      isParent
                    />
                  </div>
                )}
              >
                {item.items.map((child) => (
                  <div key={child.key} className="o-userDropdown_sub">
                    <MenuItem
                      label={child.title || ''}
                      link={child.path}
                      target={child.target}
                      onClose={onClose}
                      isChild
                    />
                  </div>
                ))}
              </Panel>
            </Collapse>
          ) : (
            <MenuItem
              key={item.key}
              icon={item.icon?.inactive}
              label={item.title || ''}
              link={item.path}
              target={item.target}
              onClose={onClose}
            />
          );
        }))}

      </div>

      {/* Language */}
      {/* <div className="o-userDropdown_language">
        <Space>
          <GlobeSimple size={20} />
          <Typography.Text>Language</Typography.Text>
        </Space>
        <LanguageToggle value={lang} onChange={setLang} />

      </div> */}

      <Divider />

      {/* Logout */}
      <div className="o-userDropdown_logout" onClick={onLogout}>
        <SignOut size={20} />
        <Typography.Text>Sign out</Typography.Text>
      </div>
    </div>
  );
};

export default UserDropdown;
